import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { HttpHelper } from '@umss/shared/utils';
import { Observable, Subject } from 'rxjs';

import {
  AuthenticationResponseDto,
  UserRole,
} from '../../../models/auth.model';
import { AUTH_ERROR } from '../auth-errors.model';

// --- CONFIGURATION CONSTANTS ---
const LINKEDIN_CONFIG = {
  // PUBLIC CLIENT ID
  CLIENT_ID: '78fxz3xn497r7y',
  REDIRECT_URI: 'http://localhost:4200/auth/linkedin-callback',
  SCOPE: 'openid profile email',
  AUTHORIZATION_URL: 'https://www.linkedin.com/oauth/v2/authorization',
};

@Injectable({ providedIn: 'root' })
export class LinkedinHandlerService {
  private http = inject(HttpClient);
  private userRole?: UserRole;
  private readonly baseUrl = '/api/Auth/linkedin-login';
  private callbackSubject = new Subject<AuthenticationResponseDto | null>();
  // A secure method to generate a random string for the 'state' parameter
  private generateState(length = 32): string {
    const chars =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  /**
   * Initiates the LinkedIn OAuth flow by generating a state and redirecting the user.
   */
  public initiateAuthFlow(
    userRole: UserRole
  ): Observable<AuthenticationResponseDto | null> {
    this.userRole = userRole;
    const state = this.generateState();

    // CRITICAL: Store the state for security (CSRF check upon callback)
    sessionStorage.setItem('linkedin_oauth_state', state);

    const params = HttpHelper.toHttpParams({
      response_type: 'code',
      client_id: LINKEDIN_CONFIG.CLIENT_ID,
      redirect_uri: LINKEDIN_CONFIG.REDIRECT_URI,
      state: state,
      scope: LINKEDIN_CONFIG.SCOPE,
    });

    const url = `${LINKEDIN_CONFIG.AUTHORIZATION_URL}?${params.toString()}`;

    // Redirect the user's browser to LinkedIn
    window.location.href = url;

    return this.callbackSubject.asObservable();
  }

  /**
   * Handles the redirect back from LinkedIn (the callback).
   * It performs the security check and calls the backend to finalize authentication.
   * @param code The authorization code from LinkedIn.
   * @param state The state parameter from LinkedIn.
   */
  public handleCallback(code: string, state: string): void {
    const savedState = sessionStorage.getItem('linkedin_oauth_state');
    sessionStorage.removeItem('linkedin_oauth_state');

    // 1. SECURITY CHECK (CSRF Prevention)
    if (state !== savedState) {
      this.callbackSubject.error({ error: AUTH_ERROR.CSRF_CHECK_FAILED });
      return;
    }

    // 2. Call Backend to Exchange Code for App Session Token
    // The backend is responsible for calling LinkedIn's token endpoint and issuing a JWT for our application.

    this.http
      .post<AuthenticationResponseDto>(this.baseUrl, {
        code: code,
        redirectUri: LINKEDIN_CONFIG.REDIRECT_URI,
        role: this.userRole,
      })
      .subscribe({
        next: (response) => {
          this.callbackSubject.next(response);
          this.callbackSubject.complete();
        },
        error: (error) => {
          this.callbackSubject.error(error);
        },
      });
  }
}
